import React, { useEffect, useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { api, inr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const CATEGORIES = ["Truffles", "Simple Flavourful", "Stick Chocolates", "Festive Signature", "Bonbons", "Assorted", "Whimsical", "Snacking"];
const OFFERS = [["general", "General"], ["festive", "Festive offer"], ["first_order", "First-time customers"], ["bulk", "Bulk orders"]];
const BLANK = { code: "", description: "", type: "percent", value: "", min_order: "", max_discount: "", starts_at: "", expires_at: "",
  usage_limit: "", per_customer_limit: "", offer_type: "general", bulk_min_qty: "", eligible_categories: [], eligible_product_ids: [], active: true };

const toLocal = (iso) => (iso ? new Date(iso).toLocaleString("sv-SE", { hour12: false }).slice(0, 16).replace(" ", "T") : "");
const toIso = (v) => (v ? new Date(v).toISOString() : null);
const F = ({ label, children, span }) => (
  <label className={`block ${span ? "sm:col-span-2" : ""}`}><span className="text-[10px] text-[#8C7B6E] uppercase tracking-wider">{label}</span>{children}</label>
);

const status = (c) => {
  const now = new Date();
  if (!c.active) return ["Inactive", "text-[#8C7B6E]"];
  if (c.starts_at && new Date(c.starts_at) > now) return ["Scheduled", "text-[#D97706]"];
  if (c.expires_at && new Date(c.expires_at) < now) return ["Expired", "text-[#DC2626]"];
  if (c.usage_limit && (c.used_count || 0) >= c.usage_limit) return ["Limit reached", "text-[#DC2626]"];
  return ["Live", "text-[#2E7D32]"];
};

const Chips = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5 mt-1" data-testid={testid}>
    {options.map(([id, label]) => {
      const on = value.includes(id);
      return (
        <button type="button" key={id} onClick={() => onChange(on ? value.filter((x) => x !== id) : [...value, id])}
          className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${on ? "bg-[#D4AF37] text-[#1A0F0B] border-[#D4AF37]" : "border-[#3B251D] text-[#C8B8AB] hover:border-[#D4AF37]"}`}>{label}</button>
      );
    })}
  </div>
);

const PromoForm = ({ form, setForm, products, editing }) => {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <F label="Code"><Input data-testid="promo-code" disabled={editing} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s/g, "") })} className="inp" /></F>
      <F label="Offer type">
        <select data-testid="promo-offer-type" value={form.offer_type} onChange={set("offer_type")} className="inp w-full h-10 rounded-md px-3">{OFFERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      </F>
      <F label="Description (internal)" span><Input value={form.description} onChange={set("description")} className="inp" /></F>
      <F label="Discount type">
        <select data-testid="promo-type" value={form.type} onChange={set("type")} className="inp w-full h-10 rounded-md px-3"><option value="percent">Percentage %</option><option value="flat">Fixed amount ₹</option></select>
      </F>
      <F label={form.type === "percent" ? "Discount %" : "Discount ₹"}><Input data-testid="promo-value" type="number" value={form.value} onChange={set("value")} className="inp" /></F>
      <F label="Minimum order ₹"><Input data-testid="promo-min-order" type="number" value={form.min_order} onChange={set("min_order")} className="inp" /></F>
      <F label="Maximum discount ₹ (0 = no cap)"><Input data-testid="promo-max-discount" type="number" value={form.max_discount} onChange={set("max_discount")} className="inp" /></F>
      <F label="Starts"><Input data-testid="promo-starts" type="datetime-local" value={form.starts_at} onChange={set("starts_at")} className="inp" /></F>
      <F label="Expires"><Input data-testid="promo-expires" type="datetime-local" value={form.expires_at} onChange={set("expires_at")} className="inp" /></F>
      <F label="Total usage limit (0 = unlimited)"><Input data-testid="promo-usage-limit" type="number" value={form.usage_limit} onChange={set("usage_limit")} className="inp" /></F>
      <F label="Per-customer limit (0 = unlimited)"><Input data-testid="promo-per-customer" type="number" value={form.per_customer_limit} onChange={set("per_customer_limit")} className="inp" /></F>
      {form.offer_type === "bulk" && <F label="Minimum boxes in cart"><Input data-testid="promo-bulk-qty" type="number" value={form.bulk_min_qty} onChange={set("bulk_min_qty")} className="inp" /></F>}
      <F label="Eligible categories (none = all)" span>
        <Chips testid="promo-categories" options={CATEGORIES.map((c) => [c, c])} value={form.eligible_categories} onChange={(v) => setForm({ ...form, eligible_categories: v })} />
      </F>
      <F label="Eligible products (none = all)" span>
        <div className="max-h-36 overflow-y-auto border border-[#3B251D] rounded-lg p-2 mt-1">
          <Chips testid="promo-products" options={products.map((p) => [p.product_id, p.name])} value={form.eligible_product_ids} onChange={(v) => setForm({ ...form, eligible_product_ids: v })} />
        </div>
      </F>
      <label className="flex items-center gap-2 text-sm text-[#C8B8AB]"><Switch data-testid="promo-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Active</label>
    </div>
  );
};

export const PromoManager = () => {
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(BLANK);
  const load = () => api.get("/admin/coupons").then((r) => setList(r.data));
  useEffect(() => { load(); api.get("/admin/products", { params: { limit: 200 } }).then((r) => setProducts(r.data.items)); }, []);

  const openNew = () => { setForm(BLANK); setEditing(false); setOpen(true); };
  const openEdit = (c) => {
    setForm({ ...BLANK, ...c, starts_at: toLocal(c.starts_at), expires_at: toLocal(c.expires_at),
      eligible_categories: c.eligible_categories || [], eligible_product_ids: c.eligible_product_ids || [] });
    setEditing(true); setOpen(true);
  };
  const save = async () => {
    const n = (v) => Number(v || 0);
    const body = { ...form, value: n(form.value), min_order: n(form.min_order), max_discount: n(form.max_discount), usage_limit: n(form.usage_limit),
      per_customer_limit: n(form.per_customer_limit), bulk_min_qty: n(form.bulk_min_qty), starts_at: toIso(form.starts_at), expires_at: toIso(form.expires_at) };
    delete body.used_count; delete body.created_at;
    try {
      if (editing) await api.put(`/admin/coupons/${form.code}`, body);
      else await api.post("/admin/coupons", body);
      toast.success(editing ? "Promo code updated" : "Promo code created");
      setOpen(false); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg?.replace("Value error, ", "") : d || "Save failed");
    }
  };
  const toggle = async (c) => { await api.patch(`/admin/coupons/${c.code}/active`, { active: !c.active }); load(); };
  const del = async (c) => { if (!window.confirm(`Delete ${c.code}?`)) return; await api.delete(`/admin/coupons/${c.code}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="admin-coupons">
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-display text-3xl">Promo Codes</h1>
        <Button onClick={openNew} data-testid="promo-new-btn" className="btn-gold rounded-full"><Plus size={16} className="mr-2" /> New Promo Code</Button>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((c) => {
          const [label, cls] = status(c);
          return (
            <div key={c.code} className="lux-card p-5" data-testid={`coupon-${c.code}`}>
              <div className="flex justify-between items-start gap-2">
                <div>
                  <p className="font-mono-lux text-[#D4AF37] text-lg">{c.code}</p>
                  <p className={`text-[11px] font-semibold ${cls}`} data-testid={`coupon-status-${c.code}`}>{label} · {OFFERS.find((o) => o[0] === (c.offer_type || "general"))[1]}</p>
                </div>
                <Switch data-testid={`coupon-toggle-${c.code}`} checked={c.active} onCheckedChange={() => toggle(c)} />
              </div>
              <p className="text-[#C8B8AB] text-sm mt-3">{c.type === "percent" ? `${c.value}% off` : `${inr(c.value)} off`}{c.max_discount ? ` (max ${inr(c.max_discount)})` : ""}{c.min_order ? ` · min ${inr(c.min_order)}` : ""}</p>
              <p className="text-[#8C7B6E] text-xs mt-1">Used {c.used_count || 0}{c.usage_limit ? ` / ${c.usage_limit}` : ""}{c.per_customer_limit ? ` · ${c.per_customer_limit} per customer` : ""}</p>
              {(c.starts_at || c.expires_at) && <p className="text-[#8C7B6E] text-xs">{c.starts_at ? new Date(c.starts_at).toLocaleDateString("en-IN") : "Now"} → {c.expires_at ? new Date(c.expires_at).toLocaleDateString("en-IN") : "No expiry"}</p>}
              {(c.eligible_categories?.length > 0 || c.eligible_product_ids?.length > 0) && <p className="text-[#8C7B6E] text-xs truncate">Only: {[...(c.eligible_categories || []), ...(c.eligible_product_ids || []).map((id) => products.find((p) => p.product_id === id)?.name || id)].join(", ")}</p>}
              <div className="flex gap-4 mt-4">
                <button onClick={() => openEdit(c)} data-testid={`coupon-edit-${c.code}`} className="text-[#C8B8AB] hover:text-[#D4AF37] text-xs flex items-center gap-1"><Pencil size={13} /> Edit</button>
                <button onClick={() => del(c)} data-testid={`coupon-delete-${c.code}`} className="text-[#C8B8AB] hover:text-[#DC2626] text-xs flex items-center gap-1"><Trash2 size={13} /> Delete</button>
              </div>
            </div>
          );
        })}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="promo-dialog" aria-describedby={undefined}>
          <DialogTitle className="font-display text-2xl">{editing ? `Edit ${form.code}` : "New Promo Code"}</DialogTitle>
          <PromoForm form={form} setForm={setForm} products={products} editing={editing} />
          <p className="text-[11px] text-[#8C7B6E]">Promo codes never apply to products that already have a product discount.</p>
          <Button data-testid="promo-save-btn" onClick={save} className="btn-gold rounded-full w-full">Save Promo Code</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const LeadsPanel = () => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/catalogue-leads").then((r) => setList(r.data)); }, []);
  const exportCsv = async () => {
    const r = await api.get("/admin/catalogue-leads.csv", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = "catalogue-leads.csv"; a.click();
  };
  return (
    <div data-testid="admin-leads">
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-display text-3xl">Catalogue Leads <span className="text-[#8C7B6E] text-lg">({list.length})</span></h1>
        <Button onClick={exportCsv} data-testid="leads-export-btn" className="btn-gold rounded-full">Export CSV</Button>
      </div>
      <div className="lux-card overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]">
          <thead className="bg-[#1A0F0B] text-[#8C7B6E] text-xs uppercase"><tr><th className="text-left p-4">Date</th><th className="text-left p-4">Name</th><th className="text-left p-4">WhatsApp</th><th className="text-left p-4">City</th></tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={4} className="p-4 text-[#8C7B6E]">No downloads yet.</td></tr>}
            {list.map((l) => (
              <tr key={l.lead_id} className="border-t border-[#3B251D]" data-testid={`lead-${l.lead_id}`}>
                <td className="p-4 text-[#8C7B6E]">{new Date(l.created_at).toLocaleString("en-IN")}</td>
                <td className="p-4 text-[#FDFBF7]">{l.name}</td>
                <td className="p-4"><a href={`https://wa.me/${l.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-[#25D366]">{l.whatsapp}</a></td>
                <td className="p-4 text-[#C8B8AB]">{l.city || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
